import { useOktaAuthContext } from '../contexts/OktaAuthContext.tsx';
import axios from 'axios';
//@ts-ignore
import qs from 'qs';
import { EventSource } from 'eventsource';

const URL_BASE = import.meta.env.VITE_BE_BASE_URL + 'soiling-mitigation/';
const genUserProfileURL = () => `${URL_BASE}user-profile`;
const genProjectsURL = () => `${URL_BASE}projects`;
const genProjectURL = (id: string) => `${URL_BASE}projects/${id}`;
const genSoilingAssessmentURL = () => `${URL_BASE}soiling/assessment`;
const genCleaningPackagesURL = () => `${URL_BASE}cleaning/packages`;
const genMitigationStrategySessionURL = () => `${URL_BASE}mitigation-strategy-session`;
const genMitigationStrategyURL = () => `${URL_BASE}mitigation-strategy`;
const genOperationalStrategySessionURL = () => `${URL_BASE}operational-strategy-session`;
const genOperationalStrategyURL = () => `${URL_BASE}operational-strategy`;
const genOperationalStrategyExPostSessionURL = () => `${URL_BASE}ex-post-analysis-session`;
const genOperationalStrategyExPostURL = () => `${URL_BASE}ex-post-analysis`;
const genValidateCSVURL = () => `${URL_BASE}tools/validate-csv`;
const uploadSoilingSensorDataFILE = () => `${URL_BASE}files`;
const genSensorDataURL = () => `${URL_BASE}sensor-data`;

const progressCallbacks = new Map<string, (progressMessage: string) => void>();

export const Server = () => {
  const { token } = useOktaAuthContext();

  const registerProgressCallback = (sessionType: string, callback: (progressMessage: string) => void) => {
    progressCallbacks.set(sessionType, callback);
  };

  const unregisterProgressCallback = (sessionType: string) => {
    progressCallbacks.delete(sessionType);
  };

  const getFromServer = async (url: string) => {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const getFromServerWithParams = async (url: string, params: any) => {
    const response = await axios.get(url, {
      params: params,
      paramsSerializer: (params) => {
        return qs.stringify(params, { indices: false });
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const postToServerWithParams = async (url: string, params: any, body: any) => {
    const response = await axios.post(url, body, {
      params: params,
      paramsSerializer: (params) => {
        return qs.stringify(params, { indices: false });
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const postToServer = async (url: string, payload: any) => {
    const response = await axios.post(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const putToServer = async (url: string, payload: any) => {
    const response = await axios.put(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const deleteToServer = async (url: string) => {
    const response = await axios.delete(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const postFileToServer = async (url: string, file: any, params: any) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(url, formData, {
      params: params,
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const getUserProfileAPI = (): Promise<any> => {
    return getFromServer(genUserProfileURL());
  };

  const getProjectsAPI = () => {
    return getFromServer(genProjectsURL());
  };

  const getProjectAPI = (id: string) => {
    return getFromServer(genProjectURL(id));
  };

  const postProjectAPI = (payload: object) => {
    return postToServer(genProjectsURL(), payload);
  };

  const putProjectAPI = (id: string, payload: object) => {
    return putToServer(genProjectURL(id), payload);
  };

  const deleteProjectAPI = (id: string) => {
    return deleteToServer(genProjectURL(id));
  };

  const postSoilingAssessmentAPI = (params: { project_id: string }) => {
    return postToServerWithParams(genSoilingAssessmentURL(), params, {});
  };

  const getCleaningPackagesAPI = () => {
    return getFromServer(genCleaningPackagesURL()).then((data) => {
      // Recursive function to remove null values
      const removeNullValues = (obj: any): any => {
        if (obj === null || obj === undefined || obj.length === 0) {
          return undefined;
        }

        if (Array.isArray(obj)) {
          return obj.map(removeNullValues).filter((item) => item !== undefined);
        }

        if (typeof obj === 'object') {
          const cleaned: any = {};
          for (const [key, value] of Object.entries(obj)) {
            const cleanedValue = removeNullValues(value);
            if (cleanedValue !== undefined) {
              cleaned[key] = cleanedValue;
            }
          }
          return Object.keys(cleaned).length > 0 ? cleaned : undefined;
        }

        return obj;
      };

      return removeNullValues(data);
    });
  };

  const postMitigationStrategyAPI = (params: any, formData: any): Promise<any> => {
    return new Promise(async (resolve, reject) => {
      try {
        const sessionResponse = await postToServerWithParams(genMitigationStrategySessionURL(), params, formData);
        const sessionId = sessionResponse.session_id;

        const url = new URL(genMitigationStrategyURL());
        url.searchParams.append('session_id', sessionId);

        const eventSource = new EventSource(url.toString(), {
          fetch: (url, init) => {
            return fetch(url, {
              ...init,
              headers: {
                ...init.headers,
                Authorization: `Bearer ${token}`,
              },
            });
          },
        });

        let result: any = null;

        eventSource.addEventListener('start', (event) => {
          JSON.parse(event.data);
        });

        eventSource.addEventListener('result', (event) => {
          result = JSON.parse(event.data);
          eventSource.close();
          resolve(result);
        });

        eventSource.addEventListener('progress', (event) => {
          const data = JSON.parse(event.data);
          const progressCallback = progressCallbacks.get('mitigation-strategy');
          if (progressCallback && data.progress !== undefined) {
            progressCallback(data.progress);
          }
        });

        eventSource.addEventListener('error', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          console.error('Analysis error:', data);
          eventSource.close();
          reject(new Error(`Analysis failed: ${data.message}`));
        });

        eventSource.onerror = (event) => {
          console.error('EventSource connection error:', event);
          eventSource.close();
          reject(new Error('EventSource connection failed'));
        };
      } catch (error) {
        console.error('Failed to create Mitigation strategy session:', error);
        reject(error);
      }
    });
  };

  const postOperationalStrategyAPI = (params: { project_id: string }): Promise<any> => {
    return new Promise(async (resolve, reject) => {
      try {
        const sessionResponse = await postToServerWithParams(genOperationalStrategySessionURL(), params, {});
        const sessionId = sessionResponse.session_id;

        const url = new URL(genOperationalStrategyURL());
        url.searchParams.append('session_id', sessionId);

        const eventSource = new EventSource(url.toString(), {
          fetch: (url, init) => {
            return fetch(url, {
              ...init,
              headers: {
                ...init.headers,
                Authorization: `Bearer ${token}`,
              },
            });
          },
        });

        let result: any = null;

        eventSource.addEventListener('start', (event) => {
          JSON.parse(event.data);
        });

        eventSource.addEventListener('result', (event) => {
          result = JSON.parse(event.data);
          eventSource.close();
          resolve(result);
        });

        eventSource.addEventListener('progress', (event) => {
          const data = JSON.parse(event.data);
          const progressCallback = progressCallbacks.get('operational-strategy');
          if (progressCallback && data.progress !== undefined) {
            progressCallback(data.progress);
          }
        });

        eventSource.addEventListener('error', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          console.error('Analysis error:', data);
          eventSource.close();
          reject(new Error(`Analysis failed: ${data.message}`));
        });

        eventSource.onerror = (event) => {
          console.error('EventSource connection error:', event);
          eventSource.close();
          reject(new Error('EventSource connection failed'));
        };
      } catch (error) {
        console.error('Failed to create analysis session:', error);
        reject(error);
      }
    });
  };

  const postOperationalStrategyExPostAPI = (params: { project_id: string }): Promise<any> => {
    return new Promise(async (resolve, reject) => {
      try {
        const sessionResponse = await postToServerWithParams(genOperationalStrategyExPostSessionURL(), params, {});
        const sessionId = sessionResponse.session_id;

        const url = new URL(genOperationalStrategyExPostURL());
        url.searchParams.append('session_id', sessionId);

        const eventSource = new EventSource(url.toString(), {
          fetch: (url, init) => {
            return fetch(url, {
              ...init,
              headers: {
                ...init.headers,
                Authorization: `Bearer ${token}`,
              },
            });
          },
        });

        let result: any = null;

        eventSource.addEventListener('start', (event) => {
          JSON.parse(event.data);
        });

        eventSource.addEventListener('result', (event) => {
          result = JSON.parse(event.data);
          eventSource.close();
          resolve(result);
        });

        eventSource.addEventListener('progress', (event) => {
          const data = JSON.parse(event.data);
          const progressCallback = progressCallbacks.get('ex-post');
          if (progressCallback && data.progress !== undefined) {
            progressCallback(data.progress);
          }
        });

        eventSource.addEventListener('error', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          console.error('Analysis error:', data);
          eventSource.close();
          reject(new Error(`Analysis failed: ${data.message}`));
        });

        eventSource.onerror = (event) => {
          console.error('EventSource connection error:', event);
          eventSource.close();
          reject(new Error('EventSource connection failed'));
        };
      } catch (error) {
        console.error('Failed to create analysis session:', error);
        reject(error);
      }
    });
  };

  const postValidateCSVAPI = (file: any, params: any) => {
    return postFileToServer(genValidateCSVURL(), file, params);
  };

  const postSoilingSensorDataFileAPI = (file: any, params: any) => {
    return postFileToServer(uploadSoilingSensorDataFILE(), file, params);
  };

  const postSensorDataAPI = (params: { file_uid: string; file_type: string; project_id: string }) => {
    return postToServerWithParams(genSensorDataURL(), params, {});
  };

  return {
    URL_BASE,
    getFromServerWithParams,
    getUserProfileAPI,
    getProjectsAPI,
    getProjectAPI,
    postProjectAPI,
    putProjectAPI,
    deleteProjectAPI,
    postSoilingAssessmentAPI,
    getCleaningPackagesAPI,
    postMitigationStrategyAPI,
    postOperationalStrategyAPI,
    postOperationalStrategyExPostAPI,
    postValidateCSVAPI,
    postSoilingSensorDataFileAPI,
    postSensorDataAPI,
    postToServerWithParams,
    registerProgressCallback,
    unregisterProgressCallback,
  };
};
